import React, { useEffect, useState, lazy, memo, createElement } from 'react';
import { Check, Star, ShieldCheck, ArrowRight, PlayCircle, BookOpen, Clock, Heart, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
// ─── Countdown Bar ───────────────────────────────────────────────────────────
function CountdownBar() {
  const TOTAL_SECONDS = 20 * 60;
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const progress = secondsLeft / TOTAL_SECONDS;
  const isUrgent = secondsLeft < 5 * 60;
  return <div className={`relative w-full z-50 ${isUrgent ? 'bg-red-600' : 'bg-[#2D2D2D]'} text-white transition-colors duration-1000`} data-id="element-2">
      <div className="absolute bottom-0 left-0 h-0.5 bg-[#8CC63F] transition-all duration-1000 ease-linear" style={{
      width: `${progress * 100}%`
    }} data-id="element-3" />
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center" data-id="element-4">
        <span className="text-xs sm:text-sm font-medium tracking-wide uppercase opacity-90" data-id="element-5">
          🔥 Precio especial de lanzamiento hasta que termine el tiempo
        </span>
        <div className="flex items-center gap-1.5" data-id="element-6">
          <div className={`${isUrgent ? 'bg-white/20 border-white/30' : 'bg-[#8CC63F]/20 border-[#8CC63F]/40'} border rounded px-2.5 py-1 font-mono font-bold text-base sm:text-lg tabular-nums`} data-id="element-7">
            {pad(minutes)}
          </div>
          <span className="font-bold text-lg opacity-70" data-id="element-8">:</span>
          <div className={`${isUrgent ? 'bg-white/20 border-white/30' : 'bg-[#8CC63F]/20 border-[#8CC63F]/40'} border rounded px-2.5 py-1 font-mono font-bold text-base sm:text-lg tabular-nums`} data-id="element-9">
            {pad(seconds)}
          </div>
        </div>
      </div>
    </div>;
}
// ─── Button ───────────────────────────────────────────────────────────────────
const Button = ({
  children,
  variant = 'primary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2';
  const variants = {
    primary: 'bg-[#8CC63F] text-white shadow-lg shadow-[#8CC63F]/30 hover:shadow-xl hover:bg-[#7AB535] focus:ring-[#8CC63F]',
    secondary: 'bg-[#8CC63F] text-white shadow-lg shadow-[#8CC63F]/30 hover:shadow-xl hover:bg-[#7AB535] focus:ring-[#8CC63F]'
  };
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-10">
      {children}
    </button>;
};
// ─── Section ─────────────────────────────────────────────────────────────────
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-20 md:py-28 px-4 md:px-8 pt-[30px] pb-[50px] ${className}`} data-id="element-11">
    <div className="max-w-7xl mx-auto" data-id="element-12">{children}</div>
  </section>;
// ─── FAQ ──────────────────────────────────────────────────────────────────────
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick
}: FAQItemProps) {
  return <div className="bg-white rounded-2xl shadow-sm border border-[#E8E6E1] overflow-hidden transition-all duration-300 hover:shadow-md" data-id="element-13">
      <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" onClick={onClick} aria-expanded={isOpen} data-id="element-14">
        <span className="font-serif text-lg text-gray-800 font-medium pr-4" data-id="element-15">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#5F8F6F] flex-shrink-0" data-id="element-16" /> : <ChevronDown className="w-5 h-5 text-[#9FB8C7] flex-shrink-0" data-id="element-17" />}
      </button>
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-18">
        <div className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-19">{answer}</div>
      </div>
    </div>;
}
function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: '¿Funciona si mi hijo todavía no reconoce letras?',
    answer: 'Sí. El programa comienza activando sonidos antes que letras. Está diseñado para niños que todavía no iniciaron formalmente la lectura.'
  }, {
    question: '¿Y si se frustra fácilmente?',
    answer: 'Justamente trabajamos desde el juego y micro avances diarios. La idea es construir confianza, no presión.'
  }, {
    question: '¿Tengo que ser docente para aplicarlo?',
    answer: 'No. Está pensado para padres. Cada actividad viene explicada paso a paso.'
  }, {
    question: '¿Cuánto tiempo real necesito dedicar?',
    answer: 'Solo 10 minutos por día. Es un sistema diseñado para encajar en rutinas reales.'
  }, {
    question: '¿En cuánto tiempo se ven cambios?',
    answer: 'La mayoría de las familias reportan mejoras en confianza y reconocimiento de sonidos en pocas semanas.'
  }, {
    question: '¿Qué pasa si no me resulta útil?',
    answer: 'Tenés 7 días de garantía total. Si no estás conforme, te devolvemos el dinero sin preguntas.'
  }];
  return <div className="space-y-4 max-w-3xl mx-auto" data-id="element-20">
      {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-21" />)}
    </div>;
}
// ─── Floating Notification ────────────────────────────────────────────────────
const NOTIFICATIONS = [{
  name: 'Camila',
  city: 'Buenos Aires'
}, {
  name: 'Laura',
  city: 'Santiago'
}, {
  name: 'Valentina',
  city: 'Lima'
}, {
  name: 'Mariana',
  city: 'Medellín'
}, {
  name: 'Sofía',
  city: 'Montevideo'
}, {
  name: 'Daniela',
  city: 'Quito'
}, {
  name: 'Fernanda',
  city: 'Córdoba'
}, {
  name: 'Julieta',
  city: 'Bogotá'
}];
function FloatingNotification() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 5000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!isVisible) return;
    const t = setTimeout(() => setIsVisible(false), 5000);
    return () => clearTimeout(t);
  }, [isVisible, currentIndex]);
  useEffect(() => {
    if (isVisible) return;
    const delay = Math.floor(Math.random() * (40000 - 25000 + 1) + 25000);
    const t = setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % NOTIFICATIONS.length);
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(t);
  }, [isVisible]);
  const n = NOTIFICATIONS[currentIndex];
  if (!n) return null;
  return <div className={`fixed bottom-4 left-4 z-50 transition-all duration-700 ease-in-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} data-id="element-22">
      <div className="bg-white rounded-full shadow-lg py-3 px-5 flex items-center gap-3 border border-gray-100 max-w-xs sm:max-w-sm" data-id="element-23">
        <div className="bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-24">
          <CheckCircle2 className="w-4 h-4 text-[#5F8F6F]" data-id="element-25" />
        </div>
        <p className="text-sm text-gray-700 font-medium leading-tight" data-id="element-26">
          <span className="font-bold text-gray-900" data-id="element-27">{n.name}</span> de {n.city}{' '}
          accedió al programa hace unos minutos
        </p>
      </div>
    </div>;
}
// ─── Constants ────────────────────────────────────────────────────────────────
const CHECKOUT_URL = 'https://www.gaiadigitalpro.com/checkouts/cn/hWN8zFvi4hqZmUakRIjdgO8n/es-ar?_r=AQABaDpV_yd_fXtxxGi95XVzB4-AYprr5ZgyTFGxbZpusrs&preview_theme_id=163059597347';
// ─── CheckoutLink ─────────────────────────────────────────────────────────────
const checkoutLinkStyles = 'inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-[#8CC63F] text-white shadow-lg shadow-[#8CC63F]/30 hover:shadow-xl hover:bg-[#7AB535] focus:ring-[#8CC63F]';
const CheckoutLink = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`${checkoutLinkStyles} ${className}`} data-id="element-28">
    {children}
  </a>;
// ─── LandingPage ─────────────────────────────────────────────────────────────
export function LandingPage() {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = 'https://cdn.magicpatterns.com/uploads/cWSaQfFgQ4u6F7amKVMYxQ/mockup_final_bajo.webp';
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 relative overflow-x-hidden selection:bg-[#8CC63F] selection:text-white" data-id="element-29">
      {/* Background texture */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-0" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }} data-id="element-30" />

      <CountdownBar data-id="element-31" />

      <div className="relative z-10" data-id="element-32">
        {/* 1. HERO */}
        <Section className="pt-12 pb-20 md:pt-20" data-id="element-33">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-34">
            <div className="flex-1 text-center lg:text-left space-y-8" data-id="element-35">
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 mb-4" data-id="element-36">
                <div className="flex -space-x-2" data-id="element-37">
                  {[1, 2, 3].map(i => <div key={i} className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white overflow-hidden" data-id="element-38">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" loading="eager" decoding="async" width={24} height={24} data-id="element-39" />
                    </div>)}
                </div>
                <span className="text-sm font-medium text-gray-600" data-id="element-40">
                  Más de 3.000 familias ya comenzaron
                </span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-gray-900 font-medium" data-id="element-41">
                En{' '}
                <span className="inline-block bg-[#E8A895]/20 text-[#C06C55] px-3 py-1 rounded-full text-3xl md:text-5xl mx-1 transform -rotate-2" data-id="element-42">
                  21 días
                </span>{' '}
                tu hijo puede empezar a leer con más confianza — dedicando solo{' '}
                <span className="inline-block bg-[#8CC63F]/15 text-[#5A8A1A] px-3 py-1 rounded-full text-3xl md:text-5xl mx-1 transform rotate-1" data-id="element-43">
                  10 minutos por día
                </span>
                .
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0" data-id="element-44">
                Un sistema estructurado que transforma frustración en confianza
                antes de que el bloqueo lector se instale.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start pt-4" data-id="element-45">
                <p className="text-sm text-gray-500 flex items-center gap-1" data-id="element-46">
                  <ShieldCheck className="w-4 h-4 text-[#8CC63F]" data-id="element-47" />
                  Acceso inmediato + garantía 7 días
                </p>
              </div>
            </div>
            <div className="flex-1 w-full relative" data-id="element-48">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-49">
                <img src="https://cdn.magicpatterns.com/uploads/cWSaQfFgQ4u6F7amKVMYxQ/mockup_final_bajo.webp" alt="Programa de Activación Lectora Mockup" className="w-full h-auto object-cover" width={800} height={600} fetchPriority="high" loading="eager" decoding="async" data-id="element-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" data-id="element-51" />
              </div>
            </div>
          </div>
        </Section>

        {/* 2. PROBLEM */}
        <Section className="bg-white/50 rounded-[3rem] my-10 mx-4 md:mx-8 shadow-sm border border-white/60" data-id="element-52">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-53">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-54">
              Muchos padres creen que su hijo ya va a aprender solo… hasta que
              pasa esto:
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-55">
            {[{
            title: 'Evita leer',
            icon: '🫣',
            color: 'bg-[#F3E3B6]'
          }, {
            title: 'Se frustra cuando intenta',
            icon: '😤',
            color: 'bg-[#E8A895]'
          }, {
            title: 'Empieza a compararse',
            icon: '😔',
            color: 'bg-[#9FB8C7]'
          }, {
            title: 'Pierde confianza',
            icon: '📉',
            color: 'bg-gray-200'
          }].map((item, idx) => <div key={idx} className={`${item.color}/20 p-8 rounded-3xl text-center hover:transform hover:-translate-y-1 transition-transform duration-300`} data-id="element-56">
                <div className="text-4xl mb-4" data-id="element-57">{item.icon}</div>
                <h3 className="font-bold text-gray-800 text-lg" data-id="element-58">
                  {item.title}
                </h3>
              </div>)}
          </div>
          <div className="mt-12 max-w-2xl mx-auto bg-[#E8A895]/10 border border-[#E8A895]/30 rounded-2xl p-6 text-center" data-id="element-59">
            <p className="text-[#C06C55] font-medium text-lg flex items-center justify-center gap-2" data-id="element-60">
              <span className="w-2 h-2 rounded-full bg-[#C06C55]" data-id="element-61" />Y lo peor:
              empieza a creer que no es capaz.
            </p>
          </div>
        </Section>

        {/* 3. EDUCATIONAL AUTHORITY */}
        <Section data-id="element-62">
          <div className="max-w-4xl mx-auto" data-id="element-63">
            <div className="text-center mb-16" data-id="element-64">
              <h2 className="font-serif text-3xl md:text-5xl text-gray-900 mb-6" data-id="element-65">
                Lo que realmente frena la lectura no es la inteligencia.
              </h2>
              <div className="inline-block text-white px-6 py-3 rounded-full text-lg font-medium shadow-lg shadow-[#8CC63F]/20 bg-[#E58147]" data-id="element-66">
                Es la falta de activación fonológica en el momento correcto.
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-67">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 opacity-70 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-68">
                <div className="text-red-400 font-bold mb-2 uppercase tracking-wide text-sm" data-id="element-69">
                  Sin activación
                </div>
                <h3 className="text-2xl font-serif text-gray-800 mb-4" data-id="element-70">
                  Niño frustrado
                </h3>
                <p className="text-gray-600" data-id="element-71">
                  Intenta memorizar formas sin entender los sonidos. La lectura
                  se vuelve una tarea pesada y aburrida.
                </p>
              </div>
              <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-[#8CC63F]/20 relative overflow-hidden" data-id="element-72">
                <div className="absolute top-0 right-0 bg-[#8CC63F] text-white text-xs font-bold px-3 py-1 rounded-bl-xl" data-id="element-73">
                  MÉTODO PROBADO
                </div>
                <div className="text-[#8CC63F] font-bold mb-2 uppercase tracking-wide text-sm" data-id="element-74">
                  Con activación
                </div>
                <h3 className="text-2xl font-serif text-gray-800 mb-4" data-id="element-75">
                  Niño leyendo feliz
                </h3>
                <p className="text-gray-600" data-id="element-76">
                  Entiende cómo los sonidos forman palabras. Descubre que leer
                  es como descifrar un código secreto divertido.
                </p>
              </div>
            </div>
            <div className="mt-12 text-center" data-id="element-77">
              <p className="text-xl text-gray-700 font-medium" data-id="element-78">
                Esto se puede activar en casa en solo{' '}
                <span className="text-[#8CC63F] font-bold underline decoration-wavy decoration-[#8CC63F]/30" data-id="element-79">
                  10 minutos por día
                </span>
                .
              </p>
            </div>
          </div>
        </Section>

        {/* 4. HOW IT WORKS */}
        <Section className="bg-[#F3E3B6]/20 rounded-[3rem] my-10 mx-4 md:mx-8" data-id="element-80">
          <div className="text-center mb-16" data-id="element-81">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900" data-id="element-82">
              ¿Qué pasa exactamente en esos 10 minutos?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto" data-id="element-83">
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-[#8CC63F]/20 -z-10" data-id="element-84" />
            {[{
            step: '1',
            title: 'Reconoce el sonido',
            desc: 'Juegos auditivos simples para despertar el oído.',
            img: '👂'
          }, {
            step: '2',
            title: 'Lo une con una forma',
            desc: 'Asociación visual sin presión ni memorización forzada.',
            img: '🅰️'
          }, {
            step: '3',
            title: 'Forma su primera palabra',
            desc: 'El momento mágico donde todo hace clic.',
            img: '✨'
          }].map((item, idx) => <div key={idx} className="bg-white p-8 rounded-3xl shadow-sm border border-white/50 text-center relative group hover:-translate-y-2 transition-transform duration-300" data-id="element-85">
                <div className="w-12 h-12 bg-[#8CC63F] text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg shadow-[#8CC63F]/30" data-id="element-86">
                  {item.step}
                </div>
                <div className="text-4xl mb-4" data-id="element-87">{item.img}</div>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-88">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-89">{item.desc}</p>
              </div>)}
          </div>
          <div className="mt-16 max-w-3xl mx-auto border-2 border-dashed border-[#8CC63F]/30 rounded-2xl p-8 text-center bg-white/50" data-id="element-90">
            <p className="text-lg text-gray-700 font-medium" data-id="element-91">
              "No empieza leyendo. Empieza jugando con sonidos hasta que la
              lectura aparece sola."
            </p>
          </div>
        </Section>

        {/* 5. RESULTS */}
        <Section data-id="element-92">
          <div className="flex flex-col md:flex-row items-center gap-12 max-w-6xl mx-auto" data-id="element-93">
            <div className="flex-1" data-id="element-94">
              <div className="relative" data-id="element-95">
                <img src="https://cdn.magicpatterns.com/uploads/iffapsweEzH4bdxmMp9QhY/mama_e_hijo.jpg" alt="Madre e hijo leyendo" className="rounded-3xl shadow-2xl rotate-2 hover:rotate-0 transition-all duration-500" loading="lazy" width={600} height={500} decoding="async" data-id="element-96" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-xl max-w-xs transform -rotate-2" data-id="element-97">
                  <div className="flex items-center gap-2 mb-2" data-id="element-98">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" data-id="element-99" />)}
                  </div>
                  <p className="text-xs text-gray-600 italic" data-id="element-100">
                    "Nunca pensé verlo tan motivado con sus libros."
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-8" data-id="element-101">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900" data-id="element-102">
                En pocas semanas vas a notar cambios reales:
              </h2>
              <ul className="space-y-4" data-id="element-103">
                {['Más confianza al leer', 'Más interés por libros', 'Menos frustración', 'Lectura más fluida', 'Momentos sin peleas'].map((item, idx) => <li key={idx} className="flex items-center gap-4 bg-white px-6 py-4 rounded-xl shadow-sm border border-gray-50" data-id="element-104">
                    <div className="bg-[#8CC63F]/10 p-1 rounded-full" data-id="element-105">
                      <Check className="w-5 h-5 text-[#8CC63F]" data-id="element-106" />
                    </div>
                    <span className="text-lg text-gray-800 font-medium" data-id="element-107">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <div className="pt-4" data-id="element-108">
                <p className="text-gray-600 mb-6" data-id="element-109">
                  Todo esto con solo 10 minutos por día.
                </p>
                <CheckoutLink className="w-full md:w-auto" data-id="element-110">
                  Quiero empezar hoy
                </CheckoutLink>
              </div>
            </div>
          </div>
        </Section>

        {/* 6. WHAT'S INCLUDED — SISTEMA DE TOMOS */}
        <Section className="bg-[#9FB8C7]/10 rounded-[3rem] my-10 mx-4 md:mx-8" data-id="element-111">
          <div className="text-center mb-14" data-id="element-112">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-113">
              Un sistema completo dividido en 5 tomos estratégicos
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-114">
              Cada parte cumple una función específica para que el progreso sea
              real, medible y sostenible.
            </p>
          </div>

          {/* 5 Tomos */}
          <div className="space-y-5 max-w-3xl mx-auto" data-id="element-115">
            {[{
            num: '1',
            title: 'Manual para Padres',
            badgeColor: 'bg-[#F3E3B6] text-[#8A6A20]',
            stripColor: 'bg-[#F3E3B6]/40',
            items: ['Explicación clara del método', 'Qué es activación fonológica', 'Cómo acompañar sin presión', 'Qué esperar en 21 días'],
            phrase: 'Te da claridad, seguridad y dirección.'
          }, {
            num: '2',
            title: 'Plan 21 Días',
            badgeColor: 'bg-[#8CC63F] text-white',
            stripColor: 'bg-[#8CC63F]/10',
            items: ['Estructura día por día', 'Objetivo diario + actividad principal + refuerzo', 'Tiempo real: 10 minutos'],
            phrase: 'Elimina improvisación y dudas.'
          }, {
            num: '3',
            title: 'Workbook Imprimible',
            badgeColor: 'bg-[#9FB8C7] text-white',
            stripColor: 'bg-[#9FB8C7]/20',
            items: ['Actividades progresivas', 'Sonidos · Letras · Sílabas · Primeras palabras'],
            phrase: 'El niño aprende haciendo, no memorizando.'
          }, {
            num: '4',
            title: 'Checklist de Progreso',
            badgeColor: 'bg-[#E8A895] text-white',
            stripColor: 'bg-[#E8A895]/15',
            items: ['Indicadores claros de avance', 'Reconoce sonidos · Une sílabas · Lee primeras palabras'],
            phrase: 'Ves el progreso, no lo suponés.'
          }, {
            num: '5',
            title: 'Guía Anti Frustración',
            badgeColor: 'bg-[#C06C55] text-white',
            stripColor: 'bg-[#C06C55]/10',
            items: ['Qué hacer si no quiere practicar', 'Cómo evitar presión y reforzar autoestima', 'Señales reales de avance'],
            phrase: 'Protege la confianza mientras aprende.'
          }].map(tomo => <div key={tomo.num} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col sm:flex-row" data-id="element-116">
                {/* Colored strip + badge */}
                <div className={`${tomo.stripColor} flex items-center justify-center px-6 py-5 sm:py-0 sm:min-w-[88px] flex-shrink-0`} data-id="element-117">
                  <div className={`w-12 h-12 ${tomo.badgeColor} rounded-full flex items-center justify-center font-bold text-xl shadow-sm`} data-id="element-118">
                    {tomo.num}
                  </div>
                </div>
                {/* Content */}
                <div className="flex-1 px-6 py-6" data-id="element-119">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1" data-id="element-120">
                    Tomo {tomo.num}
                  </p>
                  <h3 className="font-bold text-xl text-gray-900 mb-3" data-id="element-121">
                    📂 {tomo.title}
                  </h3>
                  <ul className="space-y-1.5 mb-4" data-id="element-122">
                    {tomo.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-600 text-sm" data-id="element-123">
                        <span className="text-[#8CC63F] mt-0.5 flex-shrink-0 font-bold" data-id="element-124">
                          —
                        </span>
                        <span data-id="element-125">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-sm font-semibold text-[#5A8A1A] italic border-t border-gray-100 pt-3" data-id="element-126">
                    "{tomo.phrase}"
                  </p>
                </div>
              </div>)}
          </div>

          {/* Callout */}
          <div className="mt-12 max-w-2xl mx-auto border-2 border-dashed border-[#8CC63F]/30 rounded-2xl p-8 text-center bg-white/60" data-id="element-127">
            <p className="text-lg text-gray-900 font-semibold mb-1" data-id="element-128">
              Esto no es un PDF suelto.
            </p>
            <p className="text-gray-600" data-id="element-129">
              Es un sistema completo diseñado para funcionar en casa.
            </p>
          </div>

          <div className="mt-8 text-center" data-id="element-130">
            <CheckoutLink data-id="element-131">Quiero acceder al sistema completo</CheckoutLink>
          </div>

          {/* Bonos */}
          <div className="mt-20" data-id="element-132">
            <div className="text-center mb-10" data-id="element-133">
              <h3 className="font-serif text-2xl md:text-3xl text-gray-900 mb-2" data-id="element-134">
                Y además recibís estos 4 bonos exclusivos
              </h3>
              <p className="text-gray-500" data-id="element-135">
                Incluidos hoy sin costo adicional.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto" data-id="element-136">
              {[{
              emoji: '🎁',
              title: 'Juegos Fonológicos',
              desc: 'Actividades adicionales para reforzar sonidos de forma divertida.'
            }, {
              emoji: '🎁',
              title: 'Calendario Visual',
              desc: 'Para que el niño vea su avance y mantenga motivación.'
            }, {
              emoji: '🎁',
              title: 'Mini Biblioteca Inicial',
              desc: 'Primeras palabras y frases listas para practicar.'
            }, {
              emoji: '🎁',
              title: 'Audio Calmante',
              desc: 'Para usar antes de practicar y reducir resistencia o ansiedad.'
            }].map((bono, idx) => <div key={idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-start gap-4" data-id="element-137">
                  <span className="text-2xl flex-shrink-0" data-id="element-138">{bono.emoji}</span>
                  <div data-id="element-139">
                    <h4 className="font-bold text-gray-900 mb-1" data-id="element-140">
                      {bono.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-141">
                      {bono.desc}
                    </p>
                  </div>
                </div>)}
            </div>
            <p className="text-center text-gray-500 mt-8 text-sm" data-id="element-142">
              Todo está pensado para que el proceso sea simple, claro y
              aplicable en casa.
            </p>
          </div>
        </Section>

        {/* 7. SOCIAL PROOF */}
        <Section data-id="element-143">
          <div className="text-center mb-16" data-id="element-144">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4" data-id="element-145">
              Lo que dicen otras familias
            </h2>
            <p className="text-gray-600" data-id="element-146">
              Historias reales de mamás que recuperaron la paz en casa.
            </p>
          </div>
          <div className="max-w-5xl mx-auto mb-12" data-id="element-147">
            <img src="https://cdn.magicpatterns.com/uploads/cGpxraWxYXbeBZi4zowF7N/resenas.png" alt="Testimonios de familias" className="w-full h-auto rounded-3xl shadow-lg" loading="lazy" width={1200} height={600} decoding="async" data-id="element-148" />
          </div>
          <div className="text-center" data-id="element-149">
            <CheckoutLink data-id="element-150">
              Unirme a las familias que ya lo lograron
            </CheckoutLink>
          </div>
        </Section>

        {/* 8. PRICE */}
        <Section className="bg-[#FDFBF7] relative overflow-hidden" data-id="element-151">
          <div className="absolute inset-0 bg-[#E8A895]/5 rounded-[3rem] mx-4 md:mx-8" data-id="element-152" />
          <div className="relative z-10 max-w-lg mx-auto text-center py-12" data-id="element-153">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-8" data-id="element-154">
              Todo esto debería costar:
            </h2>
            <div className="space-y-3 mb-8 opacity-70" data-id="element-155">
              {[{
              label: 'Programa completo',
              price: '$39.900'
            }, {
              label: 'Guías y planificadores',
              price: '$14.000'
            }, {
              label: 'Bonos exclusivos',
              price: '$9.000'
            }].map((row, i) => <div key={i} className="flex justify-between items-center bg-white/60 px-6 py-3 rounded-lg" data-id="element-156">
                  <span className="text-gray-600" data-id="element-157">{row.label}</span>
                  <span className="font-bold text-gray-800 line-through" data-id="element-158">
                    {row.price}
                  </span>
                </div>)}
            </div>
            <div className="mb-8" data-id="element-159">
              <p className="text-gray-500 uppercase tracking-widest text-sm font-bold mb-2" data-id="element-160">
                Valor Real Total
              </p>
              <p className="text-2xl text-gray-400 line-through font-serif" data-id="element-161">
                $62.900
              </p>
            </div>
            <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-[#8CC63F]/20 relative overflow-hidden" data-id="element-162">
              <div className="absolute top-0 left-0 w-full bg-[#8CC63F] text-white text-sm font-bold py-1" data-id="element-163">
                OFERTA LIMITADA
              </div>
              <p className="text-gray-600 mt-6 mb-2" data-id="element-164">
                Hoy acceso completo por:
              </p>
              <div className="flex items-center justify-center gap-2 mb-6" data-id="element-165">
                <span className="text-5xl md:text-6xl font-bold text-[#8CC63F] font-serif" data-id="element-166">
                  $17.900
                </span>
              </div>
              <CheckoutLink className="w-full text-lg shadow-xl shadow-[#8CC63F]/40" data-id="element-167">
                Acceder ahora con 72% OFF
              </CheckoutLink>
              <p className="text-xs text-gray-400 mt-4" data-id="element-168">
                Pago único. Acceso de por vida.
              </p>
            </div>
          </div>
        </Section>

        {/* 9. GUARANTEE */}
        <Section className="bg-white rounded-[3rem] my-10 mx-4 md:mx-8 shadow-sm border border-gray-100" data-id="element-169">
          <div className="max-w-3xl mx-auto text-center" data-id="element-170">
            <div className="w-20 h-20 bg-[#F3E3B6] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-171">
              <ShieldCheck className="w-10 h-10 text-[#C06C55]" data-id="element-172" />
            </div>
            <div className="inline-block bg-[#F3E3B6]/30 text-[#C06C55] px-4 py-1 rounded-full text-sm font-bold mb-4" data-id="element-173">
              GARANTÍA 100% SOBRE TU INVERSIÓN
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6" data-id="element-174">
              Probalo sin riesgo durante 7 días
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-175">
              Si no ves cambios o no te resulta útil, te devolvemos el dinero.
              Sin preguntas, sin letra chica. Queremos que estés 100% tranquila.
            </p>
            <CheckoutLink className="min-w-[200px]" data-id="element-176">
              Quiero probarlo sin riesgo
            </CheckoutLink>
          </div>
        </Section>

        {/* 10. FAQ */}
        <Section className="pb-16" data-id="element-177">
          <div className="text-center mb-16" data-id="element-178">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900" data-id="element-179">
              Preguntas frecuentes
            </h2>
          </div>
          <FAQAccordion data-id="element-180" />
        </Section>

        {/* 11. RECAP + FINAL CTA */}
        <Section className="bg-[#2D2D2D] rounded-[3rem] my-10 mx-4 md:mx-8 relative overflow-hidden" data-id="element-181">
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-[#8CC63F]/5 pointer-events-none rounded-[3rem]" data-id="element-182" />
          <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-183">
            <div className="inline-flex items-center gap-2 bg-[#8CC63F]/20 border border-[#8CC63F]/30 px-4 py-2 rounded-full mb-8" data-id="element-184">
              <CheckCircle2 className="w-4 h-4 text-[#8CC63F]" data-id="element-185" />
              <span className="text-[#8CC63F] text-sm font-bold uppercase tracking-widest" data-id="element-186">
                Todo lo que recibís hoy
              </span>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-187">
              Un sistema completo para que tu hijo empiece a leer con confianza
            </h2>
            <p className="text-gray-400 mb-12 text-lg" data-id="element-188">
              Esto es exactamente lo que entra en tu acceso inmediato:
            </p>

            {/* 5 Tomos */}
            <div className="mb-10 text-left" data-id="element-189">
              <p className="text-xs font-bold uppercase tracking-widest text-[#8CC63F] mb-4 text-center" data-id="element-190">
                📚 El Sistema — 5 Tomos Estratégicos
              </p>
              <div className="space-y-3" data-id="element-191">
                {[{
                num: '1',
                title: 'Manual para Padres',
                desc: 'Claridad total sobre el método y cómo acompañar sin presión.'
              }, {
                num: '2',
                title: 'Plan 21 Días',
                desc: 'Estructura día por día. 10 minutos. Sin improvisar.'
              }, {
                num: '3',
                title: 'Workbook Imprimible',
                desc: 'Actividades progresivas: sonidos, letras, sílabas y primeras palabras.'
              }, {
                num: '4',
                title: 'Checklist de Progreso',
                desc: 'Indicadores claros para ver el avance en tiempo real.'
              }, {
                num: '5',
                title: 'Guía Anti Frustración',
                desc: 'Qué hacer cuando no quiere practicar y cómo proteger su confianza.'
              }].map(tomo => <div key={tomo.num} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl px-5 py-4" data-id="element-192">
                    <div className="w-8 h-8 bg-[#8CC63F] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5" data-id="element-193">
                      {tomo.num}
                    </div>
                    <div data-id="element-194">
                      <p className="text-white font-bold text-sm" data-id="element-195">
                        {tomo.title}
                      </p>
                      <p className="text-gray-400 text-sm mt-0.5" data-id="element-196">
                        {tomo.desc}
                      </p>
                    </div>
                    <Check className="w-5 h-5 text-[#8CC63F] flex-shrink-0 mt-0.5 ml-auto" data-id="element-197" />
                  </div>)}
              </div>
            </div>

            {/* 4 Bonos */}
            <div className="mb-10 text-left" data-id="element-198">
              <p className="text-xs font-bold uppercase tracking-widest text-[#F3E3B6] mb-4 text-center" data-id="element-199">
                🎁 4 Bonos Exclusivos sin costo adicional
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-200">
                {[{
                title: 'Juegos Fonológicos',
                desc: 'Actividades extra para reforzar sonidos jugando.'
              }, {
                title: 'Calendario Visual',
                desc: 'Para que el niño vea su propio avance.'
              }, {
                title: 'Mini Biblioteca Inicial',
                desc: 'Primeras palabras y frases para practicar.'
              }, {
                title: 'Audio Calmante',
                desc: 'Reduce resistencia antes de cada sesión.'
              }].map((bono, idx) => <div key={idx} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-3" data-id="element-201">
                    <span className="text-lg flex-shrink-0" data-id="element-202">🎁</span>
                    <div data-id="element-203">
                      <p className="text-white font-bold text-sm" data-id="element-204">
                        {bono.title}
                      </p>
                      <p className="text-gray-400 text-xs mt-0.5" data-id="element-205">
                        {bono.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Guarantee reminder */}
            <div className="flex items-center justify-center gap-3 bg-[#F3E3B6]/10 border border-[#F3E3B6]/20 rounded-2xl px-6 py-4 mb-12" data-id="element-206">
              <ShieldCheck className="w-6 h-6 text-[#F3E3B6] flex-shrink-0" data-id="element-207" />
              <p className="text-[#F3E3B6] text-sm font-medium" data-id="element-208">
                + Garantía total de 7 días. Si no estás conforme, te devolvemos
                el dinero. Sin preguntas.
              </p>
            </div>

            {/* Mental trigger block */}
            <div className="bg-[#E8A895]/10 border border-[#E8A895]/20 rounded-2xl px-6 py-5 mb-10" data-id="element-209">
              <p className="text-[#E8A895] text-base font-medium leading-relaxed" data-id="element-210">
                ⏳ Cada semana que pasa sin activación lectora, tu hijo acumula
                más frustración — y más difícil se vuelve revertirlo. Las
                familias que empezaron hoy ya están viendo los primeros cambios.
              </p>
            </div>

            {/* Price recap */}
            <div className="mb-8" data-id="element-211">
              <p className="text-gray-500 text-sm mb-1" data-id="element-212">
                Todo esto por un único pago de
              </p>
              <div className="flex items-center justify-center gap-3 mb-1" data-id="element-213">
                <span className="text-gray-500 line-through text-xl" data-id="element-214">
                  $62.900
                </span>
                <span className="font-serif text-5xl font-bold text-[#8CC63F]" data-id="element-215">
                  $17.900
                </span>
              </div>
              <p className="text-gray-500 text-xs" data-id="element-216">
                Pago único · Acceso de por vida · 72% OFF
              </p>
            </div>

            {/* Final CTA */}
            <CheckoutLink className="w-full sm:w-auto text-xl px-12 py-5 shadow-2xl shadow-[#8CC63F]/30" data-id="element-217">
              Sí, quiero empezar hoy mismo →
            </CheckoutLink>
            <p className="text-gray-500 text-xs mt-4" data-id="element-218">
              Más de 3.000 familias ya dieron este paso. ¿Y vos?
            </p>
          </div>
        </Section>

        {/* Footer */}
        <footer className="bg-[#2D2D2D] text-white py-12 text-center" data-id="element-219">
          <div className="max-w-7xl mx-auto px-4" data-id="element-220">
            <p className="font-serif text-2xl mb-4" data-id="element-221">
              Programa de Activación Lectora
            </p>
            <p className="text-gray-400 text-sm" data-id="element-222">
              © 2024 Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>

      <FloatingNotification data-id="element-223" />
    </div>;
}